import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Input,
  Modal,
  message,
  Spin,
  Divider,
  Space,
  Typography,
  Tag,
  List,
} from "antd";
import {
  ExclamationCircleOutlined,
  DownSquareOutlined,
  UpSquareOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import {
  HEADERS_EXPORT_BEN,
  POST_DOC_SUCCESS,
  POST_LAWSUIT_DETAIL,
} from "../../../API/apiUrls";

const { TextArea } = Input;

const MemoDocReturn = ({ open, close, record, handleLoad }) => {
  const userId = parseInt(localStorage.getItem("USER_ID"));
  const [loading, setLoading] = useState(false);
  const [remarkValue, setRemarkValue] = useState("");
  const [showNote, setShowNote] = useState(true);
  const toggleShowNote = () => setShowNote((prev) => !prev);
  const [dataNote, setDataNote] = useState();

  const loadData = useCallback(async () => {
    setLoading(true);
    const controller = new AbortController();

    try {
      const [lawsuitRes] = await Promise.all([
        axios.post(
          POST_LAWSUIT_DETAIL,
          {
            CONTNO: record.CONTNO,
            loanId: record.loanId,
            lawsuitId: record.lawsuitId,
          },
          {
            headers: HEADERS_EXPORT_BEN,
            signal: controller.signal,
          },
        ),
      ]);

      // Handle lawsuit response
      if (lawsuitRes?.data) {
        console.log("✅ lawsuitRes:", lawsuitRes.data);

        setDataNote(lawsuitRes.data.note || []);
        console.log(
          "✅ workLawsuitDoc:",
          lawsuitRes.data.lawsuit?.workLawsuitDoc,
        );
      } else {
        throw new Error("ไม่พบข้อมูลคดี");
      }

      // Handle leasing response
    } catch (error) {
      if (error.name !== "CanceledError") {
        console.error("❌ Error loading data:", error);
        const errorMsg = error.response?.data?.message || error.message;
        message.error(`เกิดข้อผิดพลาด: ${errorMsg}`);
      }
    } finally {
      setLoading(false);
    }

    return () => controller.abort();
  }, [record]);

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open, loadData]);

  const handleBulkSave = async () => {
    if (!record?.loanId || !record?.lawsuitId) {
      message.warning("ข้อมูลไม่ครบถ้วน");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        loanId: record.loanId,
        lawsuitId: record.lawsuitId,
        userId: userId,
        mark: remarkValue,
      };

      await postDocStatus(payload, POST_DOC_SUCCESS);
      message.success("บันทึกสำเร็จ");
      handleLoad();
      handleClose();
    } catch (err) {
      console.error("❌ Error in save:", err);
    } finally {
      setLoading(false);
    }
  };

  const postDocStatus = async (payload, apiUrl) => {
    try {
      const res = await axios.post(apiUrl, payload, {
        headers: HEADERS_EXPORT_BEN,
      });

      if (res.status === 200) {
        return res.data;
      }
    } catch (error) {
      const status = error?.response?.status;
      const serverMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error.message;

      const errorMessages = {
        400: `ข้อมูลไม่ถูกต้อง: ${serverMsg}`,
        404: "ไม่พบปลายทาง (404)",
        500: "เกิดข้อผิดพลาดจากเซิร์ฟเวอร์ (500)",
      };

      message.error(
        errorMessages[status] ||
          `ไม่สามารถส่งข้อมูลได้${status ? ` (${status})` : ""}: ${serverMsg}`,
      );

      throw error;
    }
  };

  const handleClose = () => {
    setRemarkValue("");
    setShowNote(false);
    close(false);
  };

  return (
    <Modal
      title={
        <span>
          <ExclamationCircleOutlined
            style={{ color: "#faad14", marginRight: 8 }}
          />
          ตรวจสอบเอกสาร
        </span>
      }
      open={open}
      onCancel={handleClose}
      footer={null}
      width={600}
      destroyOnHidden
    >
      <Spin spinning={loading} size="large" tip="Loading...">
        {/* แสดงหมายเหตุก่อนหน้า */}
        {dataNote?.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <Divider orientation="left">
              <Space onClick={toggleShowNote} style={{ cursor: "pointer" }}>
                <Typography.Text strong>หมายเหตุก่อนหน้า</Typography.Text>
                <Tag color="blue">
                  {dataNote.length} รายการ{" "}
                  {showNote ? <UpSquareOutlined /> : <DownSquareOutlined />}
                </Tag>
              </Space>
            </Divider>

            {showNote && (
              <List
                style={{ marginBottom: 16 }}
                bordered
                dataSource={dataNote}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      title={
                        <Space>
                          <Typography.Text strong>
                            {item.USERNAME}:
                          </Typography.Text>
                          <Typography.Text
                            type="secondary"
                            style={{ fontSize: 12 }}
                          >
                            {dayjs(item.CreatedAt).format("DD/MM/YYYY HH:mm")}
                          </Typography.Text>
                        </Space>
                      }
                      description={item.note}
                    />
                  </List.Item>
                )}
              />
            )}
          </div>
        )}

        {/* ช่องกรอกหมายเหตุ */}
        <TextArea
          placeholder="กรอกหมายเหตุ (ถ้ามี)"
          value={remarkValue}
          onChange={(e) => setRemarkValue(e.target.value)}
          rows={5}
          showCount
          maxLength={500}
        />

        {/* ปุ่ม */}
        <div style={{ marginTop: 20, textAlign: "right" }}>
          <Space>
            <Button danger onClick={handleClose} disabled={loading}>
              ปิด
            </Button>
            <Button
              type="primary"
              ghost
              onClick={handleBulkSave}
              disabled={loading}
            >
              บันทึกการแก้
            </Button>
          </Space>
        </div>
      </Spin>
    </Modal>
  );
};

export default MemoDocReturn;
